import type { PickerRow } from 'api/canonicalPicker.types'
import type { PickerSource } from 'slices/aiConversations/pickerTypes'

// ═══════════════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════════════

/** Default group ID used when groupId is not specified */
export const DEFAULT_GROUP_ID = 'default'

// ═══════════════════════════════════════════════════════════════════════════
// Shared Types
// ═══════════════════════════════════════════════════════════════════════════

export interface DataItem {
  id: string
  name: string
  [key: string]: unknown
}

// ═══════════════════════════════════════════════════════════════════════════
// Shared option filtering
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Single source of truth for client-side option filtering. Both the options
 * list and the header's "select all on page" derive "the page" from this, so
 * they never diverge when a search term is active.
 *
 * NOTE: this filters the LOADED set only. It works because the picker's REST
 * endpoints (user_tag_keys, /values, locations) return their full option list
 * in one response — there is no server-side pagination on them. If pagination
 * is ever added, this must change to a server-side query, otherwise it would
 * only filter the currently-loaded page.
 */
export const filterOptionsBySearch = (
  data: Record<string, DataItem>,
  searchTerm: string
): DataItem[] => {
  const allOptions = Object.values(data)
  const term = searchTerm.trim().toLowerCase()
  if (!term) return allOptions
  // `name` can be null on real data (e.g. a location with no name), so guard
  // before lowercasing; null-named items simply never match a search term.
  return allOptions.filter(item => item.name?.toLowerCase().includes(term))
}

// ═══════════════════════════════════════════════════════════════════════════
// Operator Types
// ═══════════════════════════════════════════════════════════════════════════

export interface OperatorOption {
  id: string
  text: string
  requiresValues?: boolean
}

// ═══════════════════════════════════════════════════════════════════════════
// Endpoint Configuration
// ═══════════════════════════════════════════════════════════════════════════

export interface EndpointConfig {
  endpoint: string
  params: Record<string, unknown>
}

// ═══════════════════════════════════════════════════════════════════════════
// Dropdown States
// ═══════════════════════════════════════════════════════════════════════════

export interface TopDropdownState {
  data: Record<string, DataItem>
  selectedItem: string | null
  /** Stored data for the selected item (separate from paginated data hash) */
  selectedItemData: DataItem | null
  searchTerm: string
  page: number
  /** Total count for pagination */
  totalCount: number
  /** Items per page */
  pageSize: number
  /** Server-driven label (e.g., "Item", "Category") */
  label: string
  /** Current selected operator */
  operator: string
  /** Available operator options from server */
  operatorOptions: OperatorOption[]
  /** Endpoint configuration for fetching data */
  endpointConfig: EndpointConfig | null
  /** Whether the dropdown is open */
  isOpen: boolean
}

export interface BottomDropdownState {
  data: Record<string, DataItem>
  selectedItems: string[]
  /** Hash of selected items data so we can display them without re-fetching */
  selectedItemsData: Record<string, DataItem>
  searchTerm: string
  page: number
  /** Total count for pagination */
  totalCount: number
  /** Items per page */
  pageSize: number
  /** Server-driven label (e.g., "Modifiers", "Toppings") */
  label: string
  /** Current selected operator */
  operator: string
  /** Available operator options from server */
  operatorOptions: OperatorOption[]
  /** Endpoint configuration for fetching data */
  endpointConfig: EndpointConfig | null
  /** Subtitle template (e.g., "with {operator} modifiers") */
  subtitle: string | null
  /** Whether to show only selected items instead of search results */
  showingSelections: boolean
  /** Whether the bottom selector is disabled (e.g., when no top selection) */
  disabled: boolean
  /** Whether the dropdown is open */
  isOpen: boolean
}

export type CanonicalLevel = 'level1' | 'level2'

/** The rows picked from one of a level's sources, in the order they were picked */
export interface SourceSelection {
  ids: string[]
  rows: Record<string, PickerRow>
}

/** A level of a picker whose levels offer a choice of source */
export interface CanonicalLevelState {
  /** The source the merchant switched to; null reads the level's default */
  source: string | null
  bySource: Record<string, SourceSelection>
  /** Picks thanx-ai sent without a label that the smart source couldn't name */
  unnamedIds: string[]
  /** Versions whose products couldn't be looked up, so level 2 sees only one of them */
  unscopedIds: string[]
}

export interface PickerState {
  top: TopDropdownState
  bottom: BottomDropdownState
  /** Only a canonical picker's cards get one, so every other picker's state stays as it was */
  canonical?: Record<CanonicalLevel, CanonicalLevelState>
  isSubmitted: boolean
}

// ═══════════════════════════════════════════════════════════════════════════
// Picker Group State (includes config for this group)
// ═══════════════════════════════════════════════════════════════════════════

export interface PickerGroupState {
  /** Server's picker_id (e.g., "picker_7783480ecdc6d4ac") - used in submission payload */
  pickerId: string
  /** Title for this picker group (from server response) */
  title: string
  /** Subtitle template for this picker group (from server response) */
  subtitle: string | null
  /** Top-level operator for this group (e.g., any/all/none for the entire picker list) */
  topOperator: string
  /** Whether this group is disabled (e.g., after confirmation) */
  disabled: boolean
  /** Whether this picker has only one level (e.g., locations) — hides bottom selector */
  singleLevel: boolean
  /** Configuration for this picker group (labels, endpoints, operator options, etc.) */
  config: GlobalConfig
}

export const createInitialPickerGroupState = (): PickerGroupState => ({
  pickerId: '',
  title: '',
  subtitle: null,
  topOperator: 'any',
  disabled: false,
  singleLevel: false,
  config: createInitialGlobalConfig(),
})

// ═══════════════════════════════════════════════════════════════════════════
// Global Configuration (shared across all picker groups)
// ═══════════════════════════════════════════════════════════════════════════

export interface GlobalConfig {
  /** Level 1 (top selector) configuration */
  level1: {
    /** Backend identifier (e.g., "items") - used in submission payload */
    id: string
    /** Human-readable label (e.g., "Item") - used in UI */
    label: string
    operatorOptions: OperatorOption[]
    endpointConfig: EndpointConfig | null
    /** Field name to use as the unique key/id from API response */
    keyField: string
    /** Field name to use as the display value from API response */
    valueField: string
    /** Read instead of the fields above when the level has any */
    sources: PickerSource[]
    /** Name of the source the level reads from */
    defaultSource: string | null
  }
  /** Level 2 (bottom selector) configuration */
  level2: {
    /** Backend identifier (e.g., "modifiers") - used in submission payload */
    id: string
    /** Human-readable label (e.g., "Modifiers") - used in UI */
    label: string
    operatorOptions: OperatorOption[]
    endpointConfig: EndpointConfig | null
    subtitle: string | null
    /** Field name to use as the unique key/id from API response */
    keyField: string
    /** Field name to use as the display value from API response */
    valueField: string
    /** Read instead of the fields above when the level has any */
    sources: PickerSource[]
    /** Name of the source the level reads from */
    defaultSource: string | null
  }
}

export const createInitialGlobalConfig = (): GlobalConfig => ({
  level1: {
    id: '',
    label: 'Item',
    operatorOptions: [],
    endpointConfig: null,
    keyField: 'id',
    valueField: 'name',
    sources: [],
    defaultSource: null,
  },
  level2: {
    id: '',
    label: 'Modifiers',
    operatorOptions: [],
    endpointConfig: null,
    subtitle: null,
    keyField: 'id',
    valueField: 'name',
    sources: [],
    defaultSource: null,
  },
})

// ═══════════════════════════════════════════════════════════════════════════
// Root Slice State (flat picker storage with group mapping)
// ═══════════════════════════════════════════════════════════════════════════

export interface PickerSliceState {
  /** All pickers indexed by globally unique pickerId */
  pickers: Record<number, PickerState>
  /** Mapping of pickerId to groupId */
  pickerToGroup: Record<number, string>
  /** Picker groups indexed by groupId (stores group-level state including config) */
  groups: Record<string, PickerGroupState>
}

export const initialState: PickerSliceState = {
  pickers: {},
  pickerToGroup: {},
  groups: {},
}

// ═══════════════════════════════════════════════════════════════════════════
// Initial State Factories
// ═══════════════════════════════════════════════════════════════════════════

export const createInitialTopState = (): TopDropdownState => ({
  data: {},
  selectedItem: null,
  selectedItemData: null,
  searchTerm: '',
  page: 1,
  totalCount: 0,
  pageSize: 50,
  label: '',
  operator: 'any',
  operatorOptions: [],
  endpointConfig: null,
  isOpen: false,
})

export const createInitialBottomState = (): BottomDropdownState => ({
  data: {},
  selectedItems: [],
  selectedItemsData: {},
  searchTerm: '',
  page: 1,
  totalCount: 0,
  pageSize: 50,
  label: '',
  operator: 'any',
  operatorOptions: [],
  endpointConfig: null,
  subtitle: null,
  showingSelections: false,
  disabled: true, // Disabled by default until top selection is made
  isOpen: false,
})

export const createInitialCanonicalLevel = (): CanonicalLevelState => ({
  source: null,
  bySource: {},
  unnamedIds: [],
  unscopedIds: [],
})

export const createInitialPickerState = (): PickerState => ({
  top: createInitialTopState(),
  bottom: createInitialBottomState(),
  isSubmitted: false,
})

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ensures a picker group exists, creating it if necessary.
 */
export const ensureGroup = (
  state: PickerSliceState,
  groupId: string
): PickerGroupState => {
  if (!state.groups[groupId]) {
    state.groups[groupId] = createInitialPickerGroupState()
  }
  return state.groups[groupId]
}

/**
 * Ensures a picker exists, creating it if necessary.
 * Also registers it with a group.
 */
export const ensurePicker = (
  state: PickerSliceState,
  pickerId: number,
  groupId: string = DEFAULT_GROUP_ID
): PickerState => {
  // Ensure the group exists
  ensureGroup(state, groupId)

  // Create picker if it doesn't exist
  if (!state.pickers[pickerId]) {
    state.pickers[pickerId] = createInitialPickerState()
  }

  // Register picker with group (if not already)
  if (!state.pickerToGroup[pickerId]) {
    state.pickerToGroup[pickerId] = groupId
  }

  return state.pickers[pickerId]
}

/**
 * Gets a picker, returning undefined if not found.
 */
export const getPicker = (
  state: PickerSliceState,
  pickerId: number
): PickerState | undefined => {
  return state.pickers[pickerId]
}

/**
 * Gets the group ID for a picker.
 */
export const getPickerGroupId = (
  state: PickerSliceState,
  pickerId: number
): string | undefined => {
  return state.pickerToGroup[pickerId]
}

/**
 * Gets a picker group, returning undefined if not found.
 */
export const getGroup = (
  state: PickerSliceState,
  groupId: string
): PickerGroupState | undefined => {
  return state.groups[groupId]
}

/**
 * Gets the group config for a picker using the pickerToGroup mapping.
 * Returns undefined if the picker or group doesn't exist.
 */
export const getGroupConfigForPicker = (
  state: PickerSliceState,
  pickerId: number
): GlobalConfig | undefined => {
  const groupId = state.pickerToGroup[pickerId]
  if (!groupId) return undefined
  return state.groups[groupId]?.config
}

/**
 * Gets all picker IDs for a group.
 */
export const getPickerIdsForGroup = (
  state: PickerSliceState,
  groupId: string
): number[] => {
  return Object.entries(state.pickerToGroup)
    .filter(([_, gId]) => gId === groupId)
    .map(([pickerId]) => Number(pickerId))
}

/**
 * Computes the next available picker ID from existing pickers.
 */
export const getNextPickerId = (state: PickerSliceState): number => {
  const existingIds = Object.keys(state.pickers).map(Number)
  return existingIds.length === 0 ? 0 : Math.max(...existingIds) + 1
}

// ═══════════════════════════════════════════════════════════════════════════
// Legacy Aliases (for backward compatibility during migration)
// ═══════════════════════════════════════════════════════════════════════════

/** @deprecated Use ensureGroup instead */
export const ensurePickerGroup = ensureGroup

/** @deprecated Use getGroup instead */
export const getPickerGroup = getGroup
